import { useMemo, useState } from "react";
import {
  Boxes,
  Plus,
  Search,
  ArrowRightLeft,
  Undo2,
  Eye,
  Pencil,
  Trash2,
  ClipboardList,
  FileText,
  Building2,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Modal } from "@/components/ui/Modal";
import { PageLoader } from "@/components/ui/Spinner";
import { useAuthStore } from "@/store/auth";
import {
  useAssets,
  useBranchesList,
  useDeleteAsset,
  useHandoverPendingCount,
  useInternalUsersList,
  useUnassignAsset,
} from "../hooks/useAssetRegistry";
import type { Asset } from "../types";
import { formatDate } from "../utils";
import { AssetFormModal } from "../components/AssetFormModal";
import { AssignAssetModal } from "../components/AssignAssetModal";
import { SetVisibilityModal } from "../components/SetVisibilityModal";
import { TransferAssetModal } from "../components/TransferAssetModal";
import { RequestEquipmentModal } from "../components/RequestEquipmentModal";
import { MyProtocolsModal } from "../components/MyProtocolsModal";
import { ManagerProtocolsModal } from "../components/ManagerProtocolsModal";
import { SearchableSelect } from "../components/SearchableSelect";

type StatusFilter = "all" | "available" | "assigned";

export function AssetRegistryPage() {
  const user = useAuthStore((s) => s.user);
  const isManager = !!user && (user.role === "director" || user.is_ceo);
  const isCeo = !!user?.is_ceo;

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [userFilter, setUserFilter] = useState("");
  const [branchFilter, setBranchFilter] = useState("");

  const params = useMemo(() => {
    const p: Record<string, string> = {};
    if (search.trim()) p.search = search.trim();
    if (statusFilter !== "all") p.availability = statusFilter;
    if (userFilter) p.assigned_to = userFilter;
    if (branchFilter) p.branch = branchFilter;
    return Object.keys(p).length ? p : undefined;
  }, [search, statusFilter, userFilter, branchFilter]);

  const { data: assets = [], isLoading } = useAssets(params);
  const deleteAsset = useDeleteAsset();
  const { data: pendingCount = 0 } = useHandoverPendingCount(isManager);

  // Filter option sources (managers: users; CEO: branches)
  const { data: userOptions = [] } = useInternalUsersList(isManager);
  const { data: branchOptions = [] } = useBranchesList(isCeo);

  // Modals
  const [formAsset, setFormAsset] = useState<Asset | null | undefined>(undefined); // undefined = closed
  const [assignAsset, setAssignAsset] = useState<Asset | null>(null);
  const [visibilityAsset, setVisibilityAsset] = useState<Asset | null>(null);
  const [transferAsset, setTransferAsset] = useState<Asset | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Asset | null>(null);
  const [requestOpen, setRequestOpen] = useState(false);
  const [myProtocolsOpen, setMyProtocolsOpen] = useState(false);
  const [managerProtocolsOpen, setManagerProtocolsOpen] = useState(false);

  const myAssets = useMemo(
    () => assets.filter((a) => a.assigned_to === user?.id),
    [assets, user?.id],
  );
  const otherAssets = useMemo(
    () => assets.filter((a) => a.assigned_to !== user?.id),
    [assets, user?.id],
  );

  return (
    <div className="p-3 sm:p-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-1">
        <div className="flex items-center gap-2">
          <Boxes size={20} className="text-[var(--color-primary)]" />
          <h1 className="text-lg font-mono font-bold uppercase tracking-wider text-[var(--color-text-primary)]">
            Ewidencja majątku
          </h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" size="sm" onClick={() => setRequestOpen(true)}>
            <FileText size={14} className="sm:mr-1.5" />
            <span className="hidden sm:inline">Poproś o sprzęt</span>
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setMyProtocolsOpen(true)}>
            <ClipboardList size={14} className="sm:mr-1.5" />
            <span className="hidden sm:inline">Moje protokoły</span>
          </Button>
          {isManager && (
            <>
              <Button variant="secondary" size="sm" onClick={() => setManagerProtocolsOpen(true)}>
                <ClipboardList size={14} className="sm:mr-1.5" />
                <span className="hidden sm:inline">Protokoły przekazania</span>
                {pendingCount > 0 && (
                  <Badge color="yellow" className="ml-1.5">
                    {pendingCount}
                  </Badge>
                )}
              </Button>
              <Button size="sm" onClick={() => setFormAsset(null)}>
                <Plus size={14} className="sm:mr-1.5" />
                <span className="hidden sm:inline">Nowy obiekt</span>
              </Button>
            </>
          )}
        </div>
      </div>
      <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-4 sm:mb-6" />

      {/* Search + filters */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[180px] max-w-sm">
          <Search
            size={15}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Szukaj obiektu…"
            className="w-full pl-9 pr-3 py-2 text-sm bg-[var(--color-bg-elevated)] border border-[var(--color-border)] text-[var(--color-text-primary)]"
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          className="px-3 py-2 text-sm bg-[var(--color-bg-elevated)] border border-[var(--color-border)] text-[var(--color-text-primary)]"
        >
          <option value="all">Status: wszystkie</option>
          <option value="available">Dostępne</option>
          <option value="assigned">Przekazane</option>
        </select>

        {isManager && (
          <div className="w-full sm:w-56">
            <SearchableSelect
              options={userOptions.map((u) => ({ value: u.id, label: u.name }))}
              value={userFilter}
              onChange={setUserFilter}
              placeholder="Użytkownik: wszyscy"
              clearLabel="Wszyscy użytkownicy"
            />
          </div>
        )}

        {isCeo && (
          <select
            value={branchFilter}
            onChange={(e) => setBranchFilter(e.target.value)}
            className="px-3 py-2 text-sm bg-[var(--color-bg-elevated)] border border-[var(--color-border)] text-[var(--color-text-primary)]"
          >
            <option value="">Oddział: wszystkie</option>
            {branchOptions.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {isLoading ? (
        <PageLoader />
      ) : isManager ? (
        <ManagerTable
          assets={assets}
          isCeo={isCeo}
          onAssign={setAssignAsset}
          onVisibility={setVisibilityAsset}
          onTransfer={setTransferAsset}
          onEdit={(a) => setFormAsset(a)}
          onDelete={setDeleteTarget}
        />
      ) : (
        <EmployeeView myAssets={myAssets} otherAssets={otherAssets} />
      )}

      {/* Modals */}
      <AssetFormModal
        open={formAsset !== undefined}
        onClose={() => setFormAsset(undefined)}
        asset={formAsset ?? undefined}
      />
      <AssignAssetModal open={!!assignAsset} onClose={() => setAssignAsset(null)} asset={assignAsset} />
      <SetVisibilityModal
        open={!!visibilityAsset}
        onClose={() => setVisibilityAsset(null)}
        asset={visibilityAsset}
      />
      <TransferAssetModal
        open={!!transferAsset}
        onClose={() => setTransferAsset(null)}
        asset={transferAsset}
      />
      <RequestEquipmentModal open={requestOpen} onClose={() => setRequestOpen(false)} />
      <MyProtocolsModal open={myProtocolsOpen} onClose={() => setMyProtocolsOpen(false)} />
      <ManagerProtocolsModal
        open={managerProtocolsOpen}
        onClose={() => setManagerProtocolsOpen(false)}
      />

      {/* Delete confirm */}
      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Usuń obiekt"
      >
        <div className="space-y-4">
          <p className="text-sm text-[var(--color-text-secondary)]">
            Czy na pewno usunąć „{deleteTarget?.name}"? Tej operacji nie można cofnąć.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>
              Anuluj
            </Button>
            <Button
              variant="danger"
              loading={deleteAsset.isPending}
              onClick={() =>
                deleteTarget &&
                deleteAsset.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) })
              }
            >
              Usuń
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ── Manager table ─────────────────────────────────────────────────────────────

function ManagerTable({
  assets,
  isCeo,
  onAssign,
  onVisibility,
  onTransfer,
  onEdit,
  onDelete,
}: {
  assets: Asset[];
  isCeo: boolean;
  onAssign: (a: Asset) => void;
  onVisibility: (a: Asset) => void;
  onTransfer: (a: Asset) => void;
  onEdit: (a: Asset) => void;
  onDelete: (a: Asset) => void;
}) {
  const unassignAsset = useUnassignAsset();

  if (assets.length === 0) {
    return <p className="text-sm text-[var(--color-text-muted)] py-8 text-center">Brak obiektów.</p>;
  }

  return (
    <div className="overflow-x-auto border border-[var(--color-border)]">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[var(--color-bg-elevated)] text-left text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)]">
            <th className="px-3 py-2">LP</th>
            <th className="px-3 py-2">Nazwa</th>
            <th className="px-3 py-2">Oddział</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2">Użytkujący</th>
            <th className="px-3 py-2">Data</th>
            <th className="px-3 py-2">Widoczność</th>
            <th className="px-3 py-2 text-right">Akcje</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--color-border)]">
          {assets.map((a) => (
            <tr key={a.id} className="text-[var(--color-text-primary)]">
              <td className="px-3 py-2 font-mono text-xs text-[var(--color-text-muted)]">{a.number}</td>
              <td className="px-3 py-2">
                <div className="font-medium">{a.name}</div>
                {a.description && (
                  <div className="text-xs text-[var(--color-text-muted)]">{a.description}</div>
                )}
              </td>
              <td className="px-3 py-2 text-xs">{a.branch_name ?? "—"}</td>
              <td className="px-3 py-2">
                {a.is_available ? (
                  <Badge color="green">Dostępny</Badge>
                ) : (
                  <Badge color="gray">Przekazany</Badge>
                )}
              </td>
              <td className="px-3 py-2">{a.assigned_to_name ?? "—"}</td>
              <td className="px-3 py-2 text-xs">{a.assigned_at ? formatDate(a.assigned_at) : "—"}</td>
              <td className="px-3 py-2">
                <Badge color={a.visibility === "all" ? "blue" : "purple"}>
                  {a.visibility === "all" ? "Wszyscy" : "Użytkujący"}
                </Badge>
              </td>
              <td className="px-3 py-2">
                <div className="flex justify-end gap-1">
                  {a.is_available ? (
                    <IconBtn title="Przekaż" onClick={() => onAssign(a)}>
                      <ArrowRightLeft size={15} />
                    </IconBtn>
                  ) : (
                    <IconBtn
                      title="Zwróć sprzęt"
                      onClick={() => unassignAsset.mutate(a.id)}
                    >
                      <Undo2 size={15} />
                    </IconBtn>
                  )}
                  <IconBtn title="Zmień widoczność" onClick={() => onVisibility(a)}>
                    <Eye size={15} />
                  </IconBtn>
                  {isCeo && (
                    <IconBtn title="Przenieś do innego oddziału" onClick={() => onTransfer(a)}>
                      <Building2 size={15} />
                    </IconBtn>
                  )}
                  <IconBtn title="Edytuj" onClick={() => onEdit(a)}>
                    <Pencil size={15} />
                  </IconBtn>
                  <IconBtn title="Usuń" onClick={() => onDelete(a)} danger>
                    <Trash2 size={15} />
                  </IconBtn>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function IconBtn({
  title,
  onClick,
  children,
  danger,
}: {
  title: string;
  onClick: () => void;
  children: React.ReactNode;
  danger?: boolean;
}) {
  return (
    <button
      title={title}
      onClick={onClick}
      className={`p-1.5 hover:bg-[var(--color-bg-elevated)] ${
        danger
          ? "text-red-600 hover:text-red-700"
          : "text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
      }`}
    >
      {children}
    </button>
  );
}

// ── Employee view ─────────────────────────────────────────────────────────────

function EmployeeView({ myAssets, otherAssets }: { myAssets: Asset[]; otherAssets: Asset[] }) {
  return (
    <div className="space-y-6">
      <section>
        <h2 className="text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)] mb-2">
          Przypisane do mnie
        </h2>
        {myAssets.length === 0 ? (
          <p className="text-sm text-[var(--color-text-muted)]">Nie masz przypisanego sprzętu.</p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {myAssets.map((a) => (
              <div key={a.id} className="border border-[var(--color-border)] p-3 bg-[var(--color-bg-surface)]">
                <div className="font-medium text-[var(--color-text-primary)]">{a.name}</div>
                {a.description && (
                  <div className="text-xs text-[var(--color-text-muted)] mt-0.5">{a.description}</div>
                )}
                <div className="text-xs text-[var(--color-text-muted)] mt-2">
                  Od: {a.assigned_at ? formatDate(a.assigned_at) : "—"}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)] mb-2">
          Dostępne obiekty
        </h2>
        {otherAssets.length === 0 ? (
          <p className="text-sm text-[var(--color-text-muted)]">Brak obiektów.</p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {otherAssets.map((a) => (
              <div key={a.id} className="border border-[var(--color-border)] p-3 bg-[var(--color-bg-surface)]">
                <div className="flex items-start justify-between gap-2">
                  <div className="font-medium text-[var(--color-text-primary)]">{a.name}</div>
                  {a.is_available ? (
                    <Badge color="green">Dostępny</Badge>
                  ) : (
                    <Badge color="gray">Przekazany</Badge>
                  )}
                </div>
                {a.description && (
                  <div className="text-xs text-[var(--color-text-muted)] mt-0.5">{a.description}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
